import { zodResolver } from '@hookform/resolvers/zod';
import {
  Download,
  Eye,
  FilePlus2,
  Loader2,
  Pencil,
  Search,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DMS_CATEGORIES,
  DMS_EXTENSIONS_BY_MIME,
  DMS_MAX_FILES_PER_SELECTION,
  DMS_MAX_FILE_BYTES,
  PERMISSIONS,
  dmsCategorySchema,
  dmsAttachmentUpdateSchema,
  type DmsAttachment,
  type DmsAttachmentUpdateInput,
  type DmsCategory,
  type DmsEntityType,
} from '@erp/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { usePermissions } from '@/features/auth';
import {
  downloadDmsAttachment,
  useDeleteDmsAttachment,
  useDmsAttachments,
  useUpdateDmsAttachment,
  useUploadDmsAttachment,
} from '@/features/dms/api/use-dms';
import { formatDateTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { DmsThumbnail } from './dms-thumbnail';
import { DocumentPreviewDialog } from './DocumentPreviewDialog';

const ACCEPTED_EXTENSIONS = [...new Set(Object.values(DMS_EXTENSIONS_BY_MIME).flat())].join(',');

function fileSizeLabel(bytes: number, locale: string): string {
  if (bytes < 1024) {
    return `${new Intl.NumberFormat(locale).format(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / 1024)} KB`;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    bytes / (1024 * 1024),
  )} MB`;
}

export interface DocumentManagerProps {
  entityType: DmsEntityType;
  entityId?: string;
  title?: string;
  allowedCategories?: DmsCategory[];
  readOnlyCategories?: readonly DmsCategory[];
}

export function DocumentManager({
  entityType,
  entityId,
  title,
  allowedCategories,
  readOnlyCategories,
}: DocumentManagerProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const inputRef = useRef<HTMLInputElement>(null);
  const categories =
    allowedCategories === undefined || allowedCategories.length === 0
      ? DMS_CATEGORIES
      : allowedCategories;
  const [uploadCategory, setUploadCategory] = useState<DmsCategory>(
    categories[0] ?? 'GENERAL_FILE',
  );
  const [search, setSearch] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<DmsAttachment | null>(null);
  const [editTarget, setEditTarget] = useState<DmsAttachment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DmsAttachment | null>(null);

  const list = useDmsAttachments({
    entityType,
    ...(entityId === undefined ? {} : { entityId }),
    search,
  });
  const upload = useUploadDmsAttachment();
  const update = useUpdateDmsAttachment();
  const remove = useDeleteDmsAttachment();
  const canWrite = can(PERMISSIONS.DMS_WRITE);
  const canDelete = can(PERMISSIONS.DMS_DELETE);
  const canPublishToPortal = entityType === 'PARTNER' || entityType === 'DOCUMENT';

  const editForm = useForm<DmsAttachmentUpdateInput>({
    resolver: zodResolver(dmsAttachmentUpdateSchema),
    defaultValues: {
      title: null,
      description: null,
      category: 'GENERAL_FILE',
      tags: [],
      portalVisible: false,
    },
  });

  function openEdit(attachment: DmsAttachment): void {
    setEditTarget(attachment);
    editForm.reset({
      title: attachment.title,
      description: attachment.description,
      category: attachment.category,
      tags: attachment.tags,
      portalVisible: attachment.portalVisible,
    });
  }

  async function uploadFiles(fileList: FileList | readonly File[]): Promise<void> {
    const files = Array.from(fileList).slice(0, DMS_MAX_FILES_PER_SELECTION);
    if (!canWrite || files.length === 0) {
      return;
    }
    if (entityType !== 'GENERAL' && entityId === undefined) {
      toast.error(t('dms.errors.entityMissing'));
      return;
    }
    const invalidSize = files.find((file) => file.size === 0 || file.size > DMS_MAX_FILE_BYTES);
    if (invalidSize !== undefined) {
      toast.error(t('dms.errors.size', { name: invalidSize.name }));
      return;
    }

    let succeeded = 0;
    for (const file of files) {
      try {
        await upload.mutateAsync({
          entityType,
          ...(entityId === undefined ? {} : { entityId }),
          category: uploadCategory,
          tags: [],
          file,
        });
        succeeded += 1;
      } catch (error) {
        toast.error(t('dms.errors.uploadFile', { name: file.name }), {
          description: error instanceof Error ? error.message : undefined,
        });
      }
    }
    if (succeeded > 0) {
      toast.success(t('dms.upload.success', { count: succeeded }));
    }
    if (inputRef.current !== null) {
      inputRef.current.value = '';
    }
  }

  async function download(attachment: DmsAttachment): Promise<void> {
    try {
      await downloadDmsAttachment(attachment);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('dms.errors.download'));
    }
  }

  const submitEdit = editForm.handleSubmit((input) => {
    if (editTarget === null) {
      return;
    }
    update.mutate(
      { id: editTarget.id, input },
      {
        onSuccess: () => {
          toast.success(t('dms.edit.success'));
          setEditTarget(null);
        },
        onError: (error) => toast.error(error.message),
      },
    );
  });

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{title ?? t('dms.manager.title')}</CardTitle>
            <CardDescription>{t('dms.manager.description')}</CardDescription>
          </div>
          <div className="relative w-full sm:w-72">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              className="pl-9"
              aria-label={t('dms.search.label')}
              placeholder={t('dms.search.placeholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {canWrite ? (
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_15rem]">
            <button
              type="button"
              className={cn(
                'flex min-h-32 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-5 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                dragActive && 'border-primary bg-primary/5',
              )}
              onClick={() => inputRef.current?.click()}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragActive(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                void uploadFiles(event.dataTransfer.files);
              }}
            >
              {upload.isPending ? (
                <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
              ) : (
                <UploadCloud className="h-6 w-6 text-primary" aria-hidden="true" />
              )}
              <span className="text-sm font-medium">{t('dms.upload.dropTitle')}</span>
              <span className="text-xs text-muted-foreground">{t('dms.upload.dropHint')}</span>
              <input
                ref={inputRef}
                type="file"
                className="sr-only"
                accept={ACCEPTED_EXTENSIONS}
                multiple
                onChange={(event) => {
                  if (event.target.files !== null) {
                    void uploadFiles(event.target.files);
                  }
                }}
              />
            </button>
            <div className="space-y-2">
              <Label htmlFor={`dms-upload-category-${entityType}-${entityId ?? 'general'}`}>
                {t('dms.fields.category')}
              </Label>
              <Select
                value={uploadCategory}
                onValueChange={(value) => setUploadCategory(dmsCategorySchema.parse(value))}
              >
                <SelectTrigger id={`dms-upload-category-${entityType}-${entityId ?? 'general'}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {t(`dms.categories.${category}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t('dms.upload.categoryHint')}</p>
            </div>
          </div>
        ) : null}

        {list.isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-56 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <div className="flex flex-col items-start gap-3 rounded-md border border-border p-6">
            <p className="text-sm text-destructive" role="alert">
              {t('dms.errors.list')}
            </p>
            <Button variant="outline" onClick={() => void list.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : list.data.data.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-border p-10 text-center">
            <FilePlus2 className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
            <p className="font-medium">{t('dms.empty.title')}</p>
            <p className="text-sm text-muted-foreground">
              {search.length > 0 ? t('dms.empty.search') : t('dms.empty.description')}
            </p>
            {canWrite && search.length === 0 ? (
              <Button onClick={() => inputRef.current?.click()}>{t('dms.upload.select')}</Button>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {list.data.data.map((attachment) => (
              <article
                key={attachment.id}
                className="overflow-hidden rounded-lg border border-border bg-card"
              >
                <button
                  type="button"
                  className="flex h-32 w-full items-center justify-center overflow-hidden bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  aria-label={t('dms.actions.previewNamed', { name: attachment.originalName })}
                  onClick={() => setPreviewTarget(attachment)}
                >
                  <DmsThumbnail attachment={attachment} />
                </button>
                <div className="space-y-3 p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium" title={attachment.originalName}>
                      {attachment.title ?? attachment.originalName}
                    </p>
                    {attachment.title === null ? null : (
                      <p className="truncate text-xs text-muted-foreground">
                        {attachment.originalName}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="secondary">{t(`dms.categories.${attachment.category}`)}</Badge>
                    {attachment.portalVisible ? (
                      <Badge variant="outline">{t('dms.portal.visible')}</Badge>
                    ) : null}
                    <span className="text-xs text-muted-foreground">
                      {fileSizeLabel(attachment.fileSize, i18n.language)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('dms.item.uploaded', {
                      date: formatDateTime(attachment.createdAt, i18n.language),
                      user: attachment.uploadedByName,
                    })}
                  </p>
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t('dms.actions.preview')}
                      aria-label={t('dms.actions.preview')}
                      onClick={() => setPreviewTarget(attachment)}
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t('dms.actions.download')}
                      aria-label={t('dms.actions.download')}
                      onClick={() => void download(attachment)}
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    {canWrite && readOnlyCategories?.includes(attachment.category) !== true ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        title={t('common.edit')}
                        aria-label={t('common.edit')}
                        onClick={() => openEdit(attachment)}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                    {canDelete && readOnlyCategories?.includes(attachment.category) !== true ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                        onClick={() => setDeleteTarget(attachment)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </CardContent>

      <DocumentPreviewDialog
        attachment={previewTarget}
        open={previewTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewTarget(null);
          }
        }}
      />

      <Dialog
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
          }
        }}
      >
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('dms.edit.title')}</DialogTitle>
            <DialogDescription>{editTarget?.originalName ?? ''}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submitEdit} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="dms-edit-title">{t('dms.fields.title')}</Label>
              <Input id="dms-edit-title" {...editForm.register('title')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dms-edit-category">{t('dms.fields.category')}</Label>
              <Select
                value={editForm.watch('category') ?? 'GENERAL_FILE'}
                onValueChange={(value) =>
                  editForm.setValue('category', dmsCategorySchema.parse(value), {
                    shouldDirty: true,
                  })
                }
              >
                <SelectTrigger id="dms-edit-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {t(`dms.categories.${category}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dms-edit-tags">{t('dms.fields.tags')}</Label>
              <Input
                id="dms-edit-tags"
                value={(editForm.watch('tags') ?? []).join(', ')}
                onChange={(event) =>
                  editForm.setValue(
                    'tags',
                    event.target.value
                      .split(',')
                      .map((tag) => tag.trim())
                      .filter((tag) => tag.length > 0),
                    { shouldDirty: true },
                  )
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dms-edit-description">{t('dms.fields.description')}</Label>
              <Textarea id="dms-edit-description" rows={4} {...editForm.register('description')} />
            </div>
            {canPublishToPortal ? (
              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-1">
                  <Label htmlFor="dms-edit-portal-visible">{t('dms.portal.label')}</Label>
                  <p className="text-xs text-muted-foreground">{t('dms.portal.hint')}</p>
                </div>
                <Switch
                  id="dms-edit-portal-visible"
                  checked={editForm.watch('portalVisible') ?? false}
                  onCheckedChange={(checked) =>
                    editForm.setValue('portalVisible', checked, { shouldDirty: true })
                  }
                />
              </div>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t('dms.delete.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('dms.delete.description', { name: deleteTarget?.originalName ?? '' })}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget === null) {
                  return;
                }
                remove.mutate(deleteTarget.id, {
                  onSuccess: () => {
                    toast.success(t('dms.delete.success'));
                    setDeleteTarget(null);
                  },
                  onError: (error) => toast.error(error.message),
                });
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
